import { DEFAULT_PALETTE } from '../../core/palette';
import { POINT_DISTRIBUTION_OPTIONS } from '../../core/pointSources';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const VORONOI_PARAMS = [
  {
    kind: 'int',
    key: 'points',
    label: 'Punkte',
    min: 8,
    max: 12000,
    step: 1,
    scale: 'log',
    default: 420,
    bulkRange: [60, 2500],
    group: 'Zellen',
    tooltip: 'Anzahl der Ankerpunkte — jeder erzeugt eine Zelle.',
  },
  {
    kind: 'enum',
    key: 'distribution',
    label: 'Verteilung',
    default: 'nearText',
    group: 'Zellen',
    options: POINT_DISTRIBUTION_OPTIONS,
  },
  {
    kind: 'float',
    key: 'distributionDistance',
    label: 'Reichweite',
    min: 5,
    max: 600,
    step: 1,
    default: 120,
    unit: 'px',
    group: 'Zellen',
    showIf: (v) => v.distribution === 'ring' || v.distribution === 'nearText',
  },
  {
    kind: 'int',
    key: 'relaxation',
    label: 'Lloyd-Relaxation',
    min: 0,
    max: 20,
    step: 1,
    default: 2,
    bulkRange: [0, 8],
    group: 'Zellen',
    tooltip: 'Schiebt jeden Punkt in den Schwerpunkt seiner Zelle — macht die Zellen gleichmässiger.',
  },

  {
    kind: 'enum',
    key: 'style',
    label: 'Darstellung',
    default: 'inset',
    group: 'Darstellung',
    options: [
      { value: 'edges', label: 'Kanten' },
      { value: 'filled', label: 'Gefüllt' },
      { value: 'inset', label: 'Geschrumpft' },
      { value: 'both', label: 'Gefüllt + Kante' },
    ],
  },
  {
    kind: 'float',
    key: 'inset',
    label: 'Schrumpfung',
    min: 0,
    max: 24,
    step: 0.25,
    default: 3,
    unit: 'px',
    group: 'Darstellung',
    showIf: (v) => v.style === 'inset',
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Kantenstärke',
    min: 0.2,
    max: 12,
    step: 0.1,
    default: 1,
    unit: 'px',
    group: 'Darstellung',
    showIf: (v) => v.style !== 'filled',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.9,
    group: 'Darstellung',
  },
  {
    kind: 'enum',
    key: 'clip',
    label: 'Textbezug',
    default: 'outside',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'all', label: 'Überall' },
      { value: 'outside', label: 'Aussen' },
      { value: 'inside', label: 'Innen' },
    ],
    tooltip: 'Ob Zellen im Text, ausserhalb oder überall gezeichnet werden.',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'area', label: 'Nach Zellfläche' },
      { value: 'angle', label: 'Nach Winkel' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'ink' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type VoronoiValues = SchemaValues<typeof VORONOI_PARAMS>;
